import { useLingui } from '@lingui/react/macro';
import { styled } from '@linaria/react';
import { IconButton } from 'app-ui/components';
import {
  IconLayoutSidebarLeftCollapse,
  IconLayoutSidebarLeftExpand,
} from 'app-ui/icon';
import { themeCssVariables, useTheme } from 'app-ui/theme';
import { useIsMobile } from 'app-ui/utilities';
import { type ReactNode, useState } from 'react';

import { LAYOUT_Z_INDEX } from '@/ui/layout/constants/LayoutZIndex';
import { NavigationDrawerItem } from '@/ui/layout/navigation-drawer/components/NavigationDrawerItem';
import { NAVIGATION_DRAWER_WIDTH } from '@/ui/layout/navigation-drawer/constants/NavigationDrawerWidth';
import { useIsNavigationDrawerCollapsed } from '@/ui/layout/navigation-drawer/hooks/useIsNavigationDrawerCollapsed';
import { isMobileNavigationDrawerOpenState } from '@/ui/layout/navigation-drawer/states/isMobileNavigationDrawerOpenState';
import { isNavigationDrawerCollapsedState } from '@/ui/layout/navigation-drawer/states/isNavigationDrawerCollapsedState';
import { navigationDrawerWidthState } from '@/ui/layout/navigation-drawer/states/navigationDrawerWidthState';
import { EdgeResizeHandle } from '@/ui/layout/resize-handle/components/EdgeResizeHandle';
import { useAtomState } from '@/ui/utilities/state/jotai/hooks/useAtomState';
import { useSetAtomState } from '@/ui/utilities/state/jotai/hooks/useSetAtomState';

const StyledDesktopDrawer = styled.nav<{ drawerWidth: number }>`
  box-sizing: border-box;
  display: flex;
  flex: none;
  flex-direction: column;
  height: 100%;
  position: relative;
  transition: width calc(${themeCssVariables.animation.duration.normal} * 1s)
    ease;
  width: ${({ drawerWidth }) => `${drawerWidth}px`};

  &[data-resizing] {
    transition: none;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const StyledMobileBackdrop = styled.div`
  background: ${themeCssVariables.background.overlayPrimary};
  inset: 0;
  opacity: 0;
  pointer-events: none;
  position: fixed;
  transition: opacity calc(${themeCssVariables.animation.duration.normal} * 1s)
    ease;
  z-index: ${LAYOUT_Z_INDEX.mobileNavigationDrawerBackdrop};

  &[data-open] {
    opacity: 1;
    pointer-events: auto;
  }
`;

const StyledMobileDrawer = styled.nav`
  background: ${themeCssVariables.background.tertiary};
  border-right: 1px solid ${themeCssVariables.border.color.medium};
  bottom: 0;
  box-shadow: ${themeCssVariables.boxShadow.strong};
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  left: 0;
  max-width: 85vw;
  position: fixed;
  top: 0;
  transform: translateX(-100%);
  transition:
    transform calc(${themeCssVariables.animation.duration.normal} * 1s) ease,
    visibility 0s linear
      calc(${themeCssVariables.animation.duration.normal} * 1s);
  visibility: hidden;
  width: ${NAVIGATION_DRAWER_WIDTH.mobile}px;
  z-index: ${LAYOUT_Z_INDEX.mobileNavigationDrawer};

  &[data-open] {
    transform: translateX(0);
    transition: transform
      calc(${themeCssVariables.animation.duration.normal} * 1s) ease;
    visibility: visible;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const StyledHeader = styled.div`
  align-items: center;
  border-bottom: 1px solid ${themeCssVariables.border.color.medium};
  box-sizing: border-box;
  display: flex;
  flex: none;
  gap: ${themeCssVariables.spacing[1]};
  height: ${themeCssVariables.spacing[12]};
  min-width: 0;
  padding: 0 ${themeCssVariables.spacing[2]};

  &[data-collapsed] {
    justify-content: center;
    padding: 0;
  }
`;

const StyledHeaderContent = styled.div`
  display: flex;
  flex: 1;
  min-width: 0;

  &[data-collapsed] {
    flex: none;
  }
`;

const StyledBody = styled.div`
  box-sizing: border-box;
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: ${themeCssVariables.spacing[4]};
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  padding: ${themeCssVariables.spacing[2]};

  &[data-collapsed] {
    align-items: center;
  }
`;

const StyledFooter = styled.div`
  box-sizing: border-box;
  display: flex;
  flex: none;
  flex-direction: column;
  gap: ${themeCssVariables.betweenSiblingsGap};
  padding: ${themeCssVariables.spacing[2]};

  &[data-collapsed] {
    align-items: center;
  }
`;

type NavigationDrawerProps = {
  header: ReactNode;
  footer: ReactNode;
  children: ReactNode;
};

export const NavigationDrawer = ({
  header,
  footer,
  children,
}: NavigationDrawerProps) => {
  const { t } = useLingui();
  const theme = useTheme();
  const isMobile = useIsMobile();
  const isCollapsed = useIsNavigationDrawerCollapsed();
  const [navigationDrawerWidth, setNavigationDrawerWidth] = useAtomState(
    navigationDrawerWidthState,
  );
  const setIsNavigationDrawerCollapsed = useSetAtomState(
    isNavigationDrawerCollapsedState,
  );
  const [isMobileNavigationDrawerOpen, setIsMobileNavigationDrawerOpen] =
    useAtomState(isMobileNavigationDrawerOpenState);
  const [isResizing, setIsResizing] = useState(false);

  const handleCollapseClick = () => {
    setIsNavigationDrawerCollapsed(true);
  };

  const handleExpandClick = () => {
    setIsNavigationDrawerCollapsed(false);
  };

  const handleResizeStart = () => {
    setIsResizing(true);
  };

  const handleResizeEnd = () => {
    setIsResizing(false);
  };

  const handleBackdropClick = () => {
    setIsMobileNavigationDrawerOpen(false);
  };

  const content = (
    <>
      <StyledHeader data-collapsed={isCollapsed || undefined}>
        <StyledHeaderContent data-collapsed={isCollapsed || undefined}>
          {header}
        </StyledHeaderContent>
        {!isCollapsed && !isMobile && (
          <IconButton
            variant="ghost"
            size="sm"
            aria-label={t`Collapse sidebar`}
            tooltip={t`Collapse sidebar`}
            onClick={handleCollapseClick}
          >
            <IconLayoutSidebarLeftCollapse
              size={theme.icon.size.md}
              stroke={theme.icon.stroke.sm}
            />
          </IconButton>
        )}
      </StyledHeader>
      <StyledBody data-collapsed={isCollapsed || undefined}>
        {isCollapsed && (
          <NavigationDrawerItem
            label={t`Expand sidebar`}
            Icon={IconLayoutSidebarLeftExpand}
            onClick={handleExpandClick}
          />
        )}
        {children}
      </StyledBody>
      <StyledFooter data-collapsed={isCollapsed || undefined}>
        {footer}
      </StyledFooter>
    </>
  );

  if (isMobile) {
    return (
      <>
        <StyledMobileBackdrop
          aria-hidden
          data-open={isMobileNavigationDrawerOpen || undefined}
          onClick={handleBackdropClick}
        />
        <StyledMobileDrawer
          aria-label={t`Navigation`}
          data-open={isMobileNavigationDrawerOpen || undefined}
          inert={!isMobileNavigationDrawerOpen}
        >
          {content}
        </StyledMobileDrawer>
      </>
    );
  }

  return (
    <StyledDesktopDrawer
      aria-label={t`Navigation`}
      drawerWidth={
        isCollapsed ? NAVIGATION_DRAWER_WIDTH.collapsed : navigationDrawerWidth
      }
      data-resizing={isResizing || undefined}
    >
      {content}
      {!isCollapsed && (
        <EdgeResizeHandle
          edge="right"
          label={t`Resize sidebar`}
          width={navigationDrawerWidth}
          minimumWidth={NAVIGATION_DRAWER_WIDTH.min}
          maximumWidth={NAVIGATION_DRAWER_WIDTH.max}
          onWidthChange={setNavigationDrawerWidth}
          onResizeStart={handleResizeStart}
          onResizeEnd={handleResizeEnd}
        />
      )}
    </StyledDesktopDrawer>
  );
};
